import { Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AVATAR_SIZE, cropToSquare, MAX_ZOOM, MIN_ZOOM, panLimit, squareArea } from '../../core/image-crop';

export interface PhotoCropData {
  file: Blob;
  fileName: string;
  /** the heading; the person's own photo when absent */
  title?: string;
}

/**
 * Choosing which square of a picture becomes the profile photo (2026-09-20): drag to move, a slider to zoom, and a
 * round window showing exactly what will be kept. The maths lives in chat/core/image-crop.ts, so this app, MedicAI
 * Connect and the referrals dashboard all cut the same square from the same picture.
 */
@Component({
  selector: 'app-photo-crop-dialog',
  templateUrl: './photo-crop-dialog.component.html',
  styleUrls: ['./photo-crop-dialog.component.scss'],
})
export class PhotoCropDialogComponent implements OnDestroy {
  @ViewChild('picture') picture?: ElementRef<HTMLImageElement>;
  /** the side of the round window, in CSS pixels; the template's box matches */
  readonly box = 300;
  readonly minZoom = MIN_ZOOM;
  readonly maxZoom = MAX_ZOOM;
  readonly src: string;
  zoom = 1;
  offsetX = 0;
  offsetY = 0;
  saving = false;
  private drag: { x: number; y: number } | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<PhotoCropDialogComponent, Blob | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: PhotoCropData,
  ) {
    this.src = URL.createObjectURL(data.file);
  }

  ngOnDestroy(): void {
    URL.revokeObjectURL(this.src);
  }

  /** The picture fills the window at zoom 1, then grows from its middle. */
  get transform(): string {
    const img = this.picture?.nativeElement;
    const scale = img ? (this.box / Math.min(img.naturalWidth || 1, img.naturalHeight || 1)) * this.zoom : this.zoom;
    return `translate(calc(-50% + ${this.offsetX}px), calc(-50% + ${this.offsetY}px)) scale(${scale})`;
  }

  onPointerDown(e: PointerEvent): void {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    this.drag = { x: e.clientX - this.offsetX, y: e.clientY - this.offsetY };
  }

  onPointerMove(e: PointerEvent): void {
    if (!this.drag) return;
    e.preventDefault();
    this.offsetX = e.clientX - this.drag.x;
    this.offsetY = e.clientY - this.drag.y;
    this.clamp();
  }

  onPointerUp(): void {
    this.drag = null;
  }

  onZoom(value: number | null): void {
    this.zoom = value ?? 1;
    this.clamp();
  }

  /** Never let an edge of the picture inside the window. */
  private clamp(): void {
    const img = this.picture?.nativeElement;
    if (!img) return;
    const limit = panLimit({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight, box: this.box, zoom: this.zoom });
    this.offsetX = Math.min(limit.x, Math.max(-limit.x, this.offsetX));
    this.offsetY = Math.min(limit.y, Math.max(-limit.y, this.offsetY));
  }

  async save(): Promise<void> {
    const img = this.picture?.nativeElement;
    if (!img || this.saving) return;
    this.saving = true;
    try {
      const area = squareArea({
        naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight, box: this.box,
        zoom: this.zoom, offsetX: this.offsetX, offsetY: this.offsetY,
      });
      this.dialogRef.close(await cropToSquare(this.data.file, area, AVATAR_SIZE));
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
